import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./Blogs.module.css";
import BlogsCard from "../../Components/BlogsCard/BlogsCard";
import { open_api } from "../../Utils/network";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../Components/Card/SearchBar";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const BlogCardSkeleton = () => (
  <div className={styles.blog_skeleton}>
    <Skeleton height={200} width="100%" style={{ marginBottom: "10px" }} />
    <Skeleton height={24} width="80%" style={{ marginBottom: "8px" }} />
    <Skeleton height={18} width="60%" style={{ marginBottom: "8px" }} />
    <Skeleton height={16} width="40%" />
  </div>
);

const LoadingSpinner = () => (
  <div className={styles.spinner_container}>
    <div className={styles.spinner}></div>
  </div>
);

const CHIPS = [
  { name: "all", label: "All" },
  { name: "sleep", label: "Sleep" },
  { name: "nutrition", label: "Nutrition" },
  { name: "development", label: "Development" },
  { name: "health", label: "Health" },
  { name: "activities", label: "Activities" },
];

function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [visibleBlogs, setVisibleBlogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChip, setSelectedChip] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const ITEMS_PER_PAGE = 12;

  const navigate = useNavigate();

  const loadMoreBlogs = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    const startIndex = (nextPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setTimeout(() => {
      const newVisibleBlogs = [
        ...visibleBlogs,
        ...filteredBlogs.slice(startIndex, endIndex),
      ];
      setVisibleBlogs(newVisibleBlogs);
      setPage(nextPage);
      setHasMore(endIndex < filteredBlogs.length);
      setIsLoadingMore(false);
    }, 300);
  }, [hasMore, isLoadingMore, page, filteredBlogs, visibleBlogs]);

  const lastBlogElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMoreBlogs();
          }
        },
        { root: null, rootMargin: "0px", threshold: 0.1 }
      );
      if (node) observer.current.observe(node);
    },
    [isLoadingMore, hasMore, loadMoreBlogs]
  );

  const handleBlogNav = (item) => {
    navigate(`/blogs/${item?.meta?.slug || item}`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getBlogDetails();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [searchQuery, selectedChip, blogs]);

  const getBlogDetails = async (query = "") => {
    setIsLoading(true);
    setError(null);
    try {
      let allBlogs = [];
      let nextUrl = null;
      let isFirstPage = true;
      let hasData = false;
      do {
        let url;
        if (isFirstPage) {
          const queryParams = new URLSearchParams();
          queryParams.append(
            "fields",
            "id,title,published_date,intro,header_image_url,tags,read_time,authors"
          );
          queryParams.append("type", "blogs.BlogPage");
          if (query) queryParams.append("q", query);
          url = `blog/blog_pages?${queryParams.toString()}`;
          isFirstPage = false;
        } else {
          const urlObj = new URL(nextUrl);
          url = `blog/blog_pages${urlObj.search}`;
        }
        const res = await open_api.get(url);
        if (res?.status == 200 || res?.status == 201 || res?.status == 204) {
          const blogData = res?.data?.data;
          if (Array.isArray(blogData) && blogData.length > 0) {
            allBlogs = Array.isArray(allBlogs)
              ? [...allBlogs, ...blogData]
              : [...blogData];
            hasData = true;
            nextUrl = res?.data?.next_page_link;
          } else if (Array.isArray(blogData) && blogData.length === 0) {
            break;
          } else {
            setError("Invalid data format received from server.");
            break;
          }
        } else {
          throw new Error(`API request failed with status: ${res?.status}`);
        }
      } while (nextUrl);

      if (Array.isArray(allBlogs) && allBlogs.length > 0) {
        setBlogs(allBlogs);
        setFilteredBlogs(allBlogs);
        setVisibleBlogs(allBlogs.slice(0, ITEMS_PER_PAGE));
        setHasMore(allBlogs.length > ITEMS_PER_PAGE);
        setPage(1);
      } else if (!hasData) {
        setBlogs([]);
        setFilteredBlogs([]);
        setVisibleBlogs([]);
        setHasMore(false);
        setPage(1);
      }
    } catch (err) {
      setError("Failed to load blogs. Please try again later.");
      setBlogs([]);
      setFilteredBlogs([]);
      setVisibleBlogs([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const filterBlogs = () => {
    if (!blogs.length) return;
    let filtered = [...blogs];
    if (selectedChip !== "all") {
      filtered = filtered.filter(
        (blog) =>
          blog.tags &&
          blog.tags.some((tag) => {
            const tagName = typeof tag === "string" ? tag : tag.name;
            return tagName?.toLowerCase() === selectedChip.toLowerCase();
          })
      );
    }
    setFilteredBlogs(filtered);
    setVisibleBlogs(filtered.slice(0, ITEMS_PER_PAGE));
    setHasMore(filtered.length > ITEMS_PER_PAGE);
    setPage(1);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    getBlogDetails(query);
  };

  const handleChipClick = (chipName) => {
    setSelectedChip(chipName);
  };

  const featuredBlog = filteredBlogs.length > 0 ? filteredBlogs[0] : null;
  const gridBlogs = visibleBlogs.slice(featuredBlog ? 1 : 0);

  return (
    <>
      {/* Hero */}
      <section className={styles.hero}>
        <span className={styles.hero_pill}>
          <span className={styles.hero_pill_dot}></span>
          Expert-backed articles for every stage
        </span>
        <h1 className={styles.hero_title}>
          Parenting{" "}
          <span className={styles.hero_title_accent}>Insights</span>
        </h1>
        <p className={styles.hero_sub}>
          Evidence-based guidance, real parent stories, and expert tips —
          curated for the moments that matter most.
        </p>
      </section>

      {/* Search */}
      <div className={styles.search_wrapper}>
        <SearchBar onSearch={handleSearch} text="Search for any blogs" />
      </div>

      {/* Chip Filters */}
      <div className={styles.chip_container}>
        {CHIPS.map((chip) => (
          <button
            key={chip.name}
            className={`${styles.chip} ${
              selectedChip === chip.name ? styles.chip_active : ""
            }`}
            onClick={() => handleChipClick(chip.name)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {error && (
        <p className={styles.error_message} role="alert">
          {error}
        </p>
      )}

      {isLoading ? (
        <div className={styles.blogs_wrapper_elements}>
          {Array(12)
            .fill(0)
            .map((_, index) => (
              <div className={styles.blog_list_container} key={index}>
                <BlogCardSkeleton />
              </div>
            ))}
        </div>
      ) : filteredBlogs.length > 0 ? (
        <>
          {/* Featured Post */}
          {featuredBlog && (
            <section className={styles.featured}>
              <div
                className={styles.featured_card}
                onClick={() => handleBlogNav(featuredBlog)}
              >
                <div className={styles.featured_img}>
                  <img
                    src={featuredBlog?.header_image_url?.url}
                    alt={featuredBlog?.title || "Featured blog post"}
                  />
                </div>
                <div className={styles.featured_body}>
                  <span className={styles.featured_tag}>Featured</span>
                  <h2 className={styles.featured_title}>
                    {featuredBlog?.title}
                  </h2>
                  <p className={styles.featured_excerpt}>
                    {featuredBlog?.intro?.length > 180
                      ? `${featuredBlog.intro.substring(0, 180)}...`
                      : featuredBlog?.intro}
                  </p>
                  <div className={styles.featured_meta}>
                    <div className={styles.featured_author}>
                      {featuredBlog?.authors?.[0]?.image && (
                        <img
                          src={featuredBlog.authors[0].image}
                          alt={featuredBlog.authors[0].name}
                          className={styles.featured_avatar}
                        />
                      )}
                      <span className={styles.featured_author_name}>
                        {featuredBlog?.authors?.[0]?.name || "MyBurgo"}
                      </span>
                    </div>
                    <span className={styles.featured_read_time}>
                      {featuredBlog?.read_time
                        ? `${featuredBlog.read_time} min read`
                        : ""}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Blog Grid */}
          <div className={styles.cards}>
            {gridBlogs.map((item, index) => (
              <div
                key={item.id || index}
                className={styles.blog_card_item}
                ref={
                  index === gridBlogs.length - 1 ? lastBlogElementRef : null
                }
                onClick={() => handleBlogNav(item)}
              >
                <BlogsCard data={item} />
              </div>
            ))}
            {isLoadingMore && <LoadingSpinner />}
          </div>

          {hasMore && !isLoadingMore && (
            <div className={styles.load_more_wrapper}>
              <button
                className={styles.load_more_btn}
                onClick={loadMoreBlogs}
              >
                Load more articles
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={styles.no_results} role="status">
          <p>No blogs found matching your criteria.</p>
          <button
            className={styles.reset_button}
            onClick={() => {
              setSelectedChip("all");
              setSearchQuery("");
              getBlogDetails();
            }}
          >
            Reset filters
          </button>
        </div>
      )}
    </>
  );
}

export default Blogs;
